import { type FC } from "react";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";

type SubmitOrCancelButtonsProps = {
  // Note: `onSubmit` is intentionally *not* included here.
  // This component is meant to be used inside a <form> element, and the form should handle submission.
  // If you think you need an `onSubmit` prop here, please put it on the <form> element instead.
  className?: string;
  disableSubmit?: boolean;
  submitText?: string;
  submitEventName?: string;
  cancelText?: string;
  cancelEventName?: string;
  onCancel?: () => void;
};

export const SubmitOrCancelButtons: FC<SubmitOrCancelButtonsProps> = ({
  className,
  disableSubmit = false,
  submitText = t("common.save_changes"),
  submitEventName,
  cancelText = t("common.cancel"),
  cancelEventName,
  onCancel,
}) => {
  const methods = useFormContext();
  const isFormValid = methods ? methods.formState.isValid : true;

  return (
    // Note that we use `flex-row-reverse` here to make the primary button appear on the right.
    // This is for accessibility reasons, as screen readers will read the buttons in DOM order:
    // first the "Save changes" button, then the "Cancel" button.
    <div className={twMerge("flex flex-row-reverse justify-start gap-2", className)}>
      <Button
        disabled={disableSubmit || !isFormValid}
        eventName={submitEventName}
        kind="blue"
        size="xs"
        title={submitText}
        type="submit"
      >
        {submitText}
      </Button>

      <Button
        eventName={cancelEventName}
        kind="white"
        size="xs"
        title={cancelText}
        onClick={onCancel}
      >
        {cancelText}
      </Button>
    </div>
  );
};
